import { motion, useScroll, useSpring } from 'framer-motion'
import { useRef } from 'react'
import { Briefcase, Calendar, MapPin, GraduationCap } from 'lucide-react'
import { getConfig } from '../../../config/configLoader'
import { useTheme } from '../../../contexts/ThemeContext'

const cardVariants = {
  offscreen: { opacity: 0, x: -30 },
  onscreen: { 
    opacity: 1, x: 0,
    transition: { type: 'spring', stiffness: 200, damping: 24 }
  }
};

const nodeVariants = {
  offscreen: { scale: 0, opacity: 0 },
  onscreen: { 
    scale: 1, opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 20 }
  }
};

export const ExperienceSection = () => {
  const experience = getConfig('experience') || [];
  const education = getConfig('education') || [];
  const { isDark } = useTheme();
  const timelineRef = useRef(null);

  // Scroll-linked timeline line
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ['start 80%', 'end 20%']
  });
  const lineProgress = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  const timelineItems = [
    ...experience.map(item => ({
      ...item,
      type: 'work',
      achievements: item.achievements || item.bulletPoints || [],
      technologies: item.technologies || item.techStack || [],
      description: item.description || item.summary || '',
    })),
    ...education.map(item => ({
      ...item,
      type: 'education',
      achievements: item.achievements || item.bulletPoints || [],
      technologies: item.technologies || item.techStack || [],
      description: item.description || item.summary || item.dissertation || '',
    }))
  ].sort((a, b) => {
    // Reverse chronological by start date (most recent first)
    const aStart = new Date(a.startDate || '0000-01-01');
    const bStart = new Date(b.startDate || '0000-01-01');
    return bStart.getTime() - aStart.getTime();
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  if (!timelineItems.length) {
    return (
      <div className={`flex items-center justify-center min-h-[400px] ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
        <p>No experience data available</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 pb-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-10"
      >
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
          Experience & Education
        </h2>
        <p className={`text-sm ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
          My professional journey and educational background
        </p>
      </motion.div>

      {/* Timeline */}
      <div className="relative" ref={timelineRef}>
        {/* Animated timeline line */}
        <div className={`absolute left-3 top-2 bottom-2 w-px ${isDark ? 'bg-neutral-800/50' : 'bg-neutral-200/50'}`} />
        <motion.div 
          className="absolute left-3 top-2 w-px bg-accent-500 origin-top"
          style={{ scaleY: lineProgress, bottom: 8 }}
        />

        <div className="space-y-8">
          {timelineItems.map((item, index) => {
            const isWork = item.type === 'work';
            return (
              <motion.div
                key={index}
                variants={cardVariants}
                initial="offscreen"
                whileInView="onscreen"
                viewport={{ once: true, margin: '-60px' }}
                className="relative pl-10"
              >
                {/* Timeline node */}
                <motion.div 
                  className={`absolute left-0 top-1 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    isDark 
                      ? 'bg-neutral-900 border-neutral-700' 
                      : 'bg-white border-neutral-300'
                  }`}
                  variants={nodeVariants}
                  initial="offscreen"
                  whileInView="onscreen"
                  viewport={{ once: true, margin: '-60px' }}
                >
                  {isWork 
                    ? <Briefcase className="w-3 h-3 text-accent-500" /> 
                    : <GraduationCap className="w-3 h-3 text-accent-500" />
                  }
                </motion.div>

                {/* Card */}
                <motion.div 
                  className={`
                    p-5 rounded-xl transition-all duration-300 border-gradient
                    ${isDark 
                      ? 'glass-futuristic hover:shadow-lg hover:shadow-accent-500/10' 
                      : 'glass-futuristic-light hover:shadow-lg hover:shadow-accent-500/15'
                    }
                  `}
                  whileHover={{ x: 4, transition: { duration: 0.2 } }}
                >
                  {/* Title & Company */}
                  <h3 className={`font-semibold mb-1 ${isDark ? 'text-neutral-100' : 'text-neutral-900'}`}>
                    {item.position || item.degree}
                  </h3>
                  <div className={`text-sm mb-2 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    {item.company || item.institution}
                  </div>
                  
                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      <Calendar className="w-3 h-3" />
                      {formatDate(item.startDate)} — {formatDate(item.endDate)}
                    </span>
                    {item.location && (
                      <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
                        <MapPin className="w-3 h-3" />
                        {item.location}
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {item.description && (
                    <p className={`text-sm leading-relaxed mb-3 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                      {item.description}
                    </p>
                  )}

                  {/* Achievements */}
                  {item.achievements?.length > 0 && (
                    <ul className="space-y-1.5 mb-3">
                      {item.achievements.map((achievement, i) => (
                        <li key={i} className={`text-sm flex items-start gap-2 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
                          <span className="text-accent-500 mt-1.5 w-1 h-1 rounded-full bg-accent-500 flex-shrink-0" />
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Technologies */}
                  {item.technologies?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {item.technologies.map((tech, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.03 }}
                          className={`px-2 py-0.5 rounded text-xs ${
                            isDark 
                              ? 'bg-neutral-800 text-neutral-400' 
                              : 'bg-neutral-100 text-neutral-600'
                          }`}
                        >
                          {tech}
                        </motion.span>
                      ))}
                    </div>
                  )}
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  )
}